import { useState, useEffect, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { EligibilityPicker } from './EligibilityPicker.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/** A payment of the bill (t182), as GET /v1/payments/transaction/eligibility/:id lists it. */
interface Txn {
  beneficiaryName: string;
  netAmount: number;
  paymentModeCode: string;
  transactionStatusCode: string;
  utrNumber: string | null;
  bankReferenceNo: string | null;
  failureReason: string | null;
  paymentDate: string | null;
}

/** L15.12 — Payment Tracking: track a payment transaction lifecycle. */
export function PaymentTrackingPage(): ReactNode {
  const { t } = useTranslation();
  const { day } = useBusinessDay();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const [params, setParams] = useSearchParams();
  const [eligId, setEligId] = useState(params.get('eligibilityId') ?? '');
  const [data, setData] = useState<Txn | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = (id: string) => {
    if (!id.trim()) return;
    setLoading(true);
    setError(null);
    setData(null);
    // Every payment of the bill is kept; the newest comes first.
    apiRequest<Txn[]>(`/v1/payments/transaction/eligibility/${id.trim()}`, { token })
      .then((rows) => setData(rows[0] ?? null))
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const id = params.get('eligibilityId');
    if (id) load(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const row = (label: string, value: ReactNode) => (
    <tr style={{ borderBottom: '1px solid var(--color-border-default)' }}>
      <td style={{ padding: 8, color: 'var(--color-text-muted)' }}>{label}</td>
      <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>{value}</td>
    </tr>
  );

  return (
    <div style={{ padding: 24, maxWidth: 640 }}>
      <h1>{t('finance.tracking.title')}</h1>
      <EligibilityPicker
        value={eligId}
        onPick={(id) => { setEligId(id); setParams(id ? { eligibilityId: id } : {}); if (id) load(id); else setData(null); }}
      />
      {error && <p style={{ color: 'red' }} role="alert">{error}</p>}
      {loading && <p>{t('finance.tracking.loading')}</p>}
      {!loading && !data && !error && <p>{t('finance.tracking.empty')}</p>}
      {data && (
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid var(--color-border-default)', borderRadius: 6 }}>
          <tbody>
            {row(t('finance.tracking.rowBeneficiary'), data.beneficiaryName ?? '—')}
            {row(t('finance.tracking.rowAmount'), money(data.netAmount))}
            {row(t('finance.tracking.rowMode'), data.paymentModeCode ?? '—')}
            {row(t('finance.tracking.rowStatus'), data.transactionStatusCode ?? '—')}
            {row(t('finance.tracking.rowUtr'), data.utrNumber ?? '—')}
            {row(t('finance.tracking.rowBankRef'), data.bankReferenceNo ?? '—')}
            {row(t('finance.tracking.rowValueDate'), day(data.paymentDate))}
            {data.failureReason && row(t('finance.tracking.rowFailure'), data.failureReason)}
          </tbody>
        </table>
      )}
    </div>
  );
}
